import { Component, EventEmitter, Input, Output } from '@angular/core';
import { faBookmark } from '@fortawesome/pro-regular-svg-icons';
import { Store } from '@ngrx/store';
import { catchError, firstValueFrom, of, Subscription } from 'rxjs';
import { loginFromBookmarksTab } from '../../actions/list.actions';
import { BookmarksSearchFacets, ListItem, ListWithItemsCount } from '../../models/list';
import { ListItemsDtoWithoutSortBy, ListService } from '../../services/list.service';
import { BookmarkSearch } from '../searchable-bookmarks-search-bar/searchable-bookmarks-search-bar.component';
import { getSearchFacets, isLastPageLoaded } from 'app/list/reducers/list.reducer';
import { ListTransformerService } from 'app/list/services/list-transformer.service';

@Component({
  selector: 'app-searchable-bookmarks-lists',
  templateUrl: './searchable-bookmarks-lists.component.html',
  styleUrls: ['./searchable-bookmarks-lists.component.scss']
})
export class SearchableBookmarksListsComponent {
  @Input() public lists: ListWithItemsCount[];
  @Input() public authorized: boolean;
  @Input() public isStaffAuthorized: boolean;
  @Output() public openList = new EventEmitter();
  @Output() public openAvailableList = new EventEmitter();
  public bookmarkIcon = faBookmark;
  public preview: ListItem[];

  // view data - search
  public listsSearch: ListWithItemsCount[] = [];
  public isSearch = false;
  public loading: boolean = false;
  public facets: BookmarksSearchFacets;
  public searchPreview: ListItem[] = [];
  private listsToSearch: ListWithItemsCount[] = [];

  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private readonly store: Store,
    private readonly listService: ListService,
    private readonly listTransformerService: ListTransformerService,
  ) {
    this.subscriptions.add(this.store.select(getSearchFacets).subscribe(facets => this.facets = facets));
  }

  ngOnInit() {
    this.preview = this.listService.generateSearchPreview(this.lists);
  }

  public ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  public logIn(event: Event): void {
    event.stopPropagation();
    this.store.dispatch(loginFromBookmarksTab());
  }

  public trackById(index: number, list: ListWithItemsCount): string {
    return list.id;
  }

  public async onSearch(search: BookmarkSearch) {
    if (search.text || search.availableOnly) {
      this.isSearch = true;
    } else {
      this.isSearch = false;
      this.loading = false;
      return;
    }
    this.loading = true;
    this.listsSearch = [];
    if (!this.listsToSearch.length) {
      await this.loadAll();
    }
    for (const list of this.listsToSearch) {
      // find matches
      this.listService.findMatches(list, search, this.facets);
      const openedListSearch = {...list, items: list.items.filter(i => i.matched)};
      openedListSearch.itemsCount = openedListSearch.items.length;
      this.listsSearch.push(openedListSearch);
    }
    this.searchPreview = this.listService.generateSearchPreview(this.listsSearch);
    this.loading = false;
  }

  public async loadAll() {
    for (const list of this.lists) {
      let nextPage = list.pagination.page + 1;
      const totalPages = list.pagination.totalPages;
      const PAGE_SIZE = 50;
      while (!isLastPageLoaded(list)) {
        // load all items
        const res$ = this.listService.getItems(list.id, 'date.asc', {pageNum: nextPage, pageSize: PAGE_SIZE})
          .pipe(catchError(() => {
            return of({
              data: [],
              page: nextPage,
              totalPages,
            } as ListItemsDtoWithoutSortBy);
          }));
        const res = await firstValueFrom(res$);
        const entities = res.data.map((item) => this.listTransformerService.transformFgOrEntityToListItemEntity(item));
        const items: ListItem[] = entities.map(e => {

          return {
            id: e.id,
            matched: false,
            selected: false,
            entity: e,
          };
        });
        list.items.push(...items);
        list.pagination.page = nextPage;
        nextPage++;
      }
      this.listsToSearch.push(list);
    }
  }
}
